// src/components/Admin/DIDDeletion.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface DIDRecord {
  did: string;
  custid: number;
  did_type: number;
  deletion_date: string | null;
  eligible_for_deletion: string | null;
  days_since_deletion: number | null;
  days_until_eligible: number | null;
  company_name: string | null;
  customer_name: string | null;
  billing_email: string | null;
  street_number: string | null;
  street_name: string | null;
  city: string | null;
  state: string | null;
}

const DIDDeletion: React.FC = () => {
  const { member } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dids, setDids] = useState<DIDRecord[]>([]);
  const [selectedDids, setSelectedDids] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [didToDelete, setDidToDelete] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Check if user is master admin
  useEffect(() => {
    if (member?.memberType !== 'master_admin') {
      setError('Access denied. Master admin privileges required.');
      setLoading(false);
      return;
    }
    fetchDIDs();
  }, [member]);

  const fetchDIDs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/did-deletion');
      setDids(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch DID deletion list');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (did: string) => {
    try {
      const response = await api.delete(`/endpoints/did-deletion/${did}`);
      if (response.data.success) {
        setSuccess(`DID ${did} successfully deleted from all systems`);
        fetchDIDs(); // Refresh the list
        setDeleteDialogOpen(false);
        setDidToDelete(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete DID');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await api.post('/endpoints/did-deletion/bulk', {
        dids: Array.from(selectedDids)
      });
      if (response.data.success) {
        setSuccess(`Successfully deleted ${response.data.success_count} DIDs. ${response.data.error_count || 0} failed.`);
        fetchDIDs();
        setSelectedDids(new Set());
        setBulkDeleteDialogOpen(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete DIDs');
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedDids(new Set(dids.map(did => did.did)));
    } else {
      setSelectedDids(new Set());
    }
  };

  const handleSelectDID = (did: string) => {
    const newSelected = new Set(selectedDids);
    if (newSelected.has(did)) {
      newSelected.delete(did);
    } else {
      newSelected.add(did);
    }
    setSelectedDids(newSelected);
  };

  const exportToCSV = () => {
    const headers = ['DID', 'Customer', 'Type', 'Deleted Date', 'Days Since', 'Status', 'E911 Address'];
    const rows = dids.map(did => [
      did.did,
      did.company_name || did.customer_name || 'Unknown',
      getTypeName(did.did_type),
      did.deletion_date || 'Unknown',
      did.days_since_deletion?.toString() || '0',
      getStatus(did),
      did.street_number ? `${did.street_number} ${did.street_name}, ${did.city}, ${did.state}` : 'No E911'
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `did_deletion_list_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getTypeName = (type: number): string => {
    const typeNames: { [key: number]: string } = {
      1: 'Standard',
      2: 'Toll-Free',
      3: 'International',
      17: 'Toll-Free',
      29: 'Deleted',
    };
    return typeNames[type] || `Type ${type}`;
  };

  const getStatus = (did: DIDRecord): string => {
    if (!did.days_since_deletion) return 'Unknown';
    return did.days_since_deletion >= 20 ? 'Eligible' : `${did.days_until_eligible || 0} days remaining`;
  };

  const getStatusColor = (did: DIDRecord): 'error' | 'warning' | 'success' => {
    if (!did.days_since_deletion) return 'warning';
    if (did.days_since_deletion >= 20) return 'error';
    if (did.days_since_deletion >= 15) return 'warning';
    return 'success';
  };

  // Calculate statistics
  const totalInactive = dids.length;
  const eligibleCount = dids.filter(d => (d.days_since_deletion || 0) >= 20).length;
  const pendingCount = dids.filter(d => (d.days_since_deletion || 0) < 20).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (member?.memberType !== 'master_admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Master admin privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">DID Deletion Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
          >
            Export to CSV
          </Button>
          <IconButton onClick={fetchDIDs} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Inactive DIDs
              </Typography>
              <Typography variant="h4">
                {totalInactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Eligible for Deletion
              </Typography>
              <Typography variant="h4" color="error">
                {eligibleCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending (&lt; 20 days)
              </Typography>
              <Typography variant="h4" color="warning.main">
                {pendingCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Checkbox
              checked={selectedDids.size === dids.length && dids.length > 0}
              indeterminate={selectedDids.size > 0 && selectedDids.size < dids.length}
              onChange={handleSelectAll}
            />
            <Typography>Select All</Typography>
            {selectedDids.size > 0 && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                Delete Selected ({selectedDids.size})
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"></TableCell>
                <TableCell>DID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Deleted Date</TableCell>
                <TableCell>Days Since</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>E911 Address</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dids.map((did) => {
                const isEligible = (did.days_since_deletion || 0) >= 20;
                return (
                  <TableRow key={did.did} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDids.has(did.did)}
                        onChange={() => handleSelectDID(did.did)}
                      />
                    </TableCell>
                    <TableCell>{did.did}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {did.company_name || did.customer_name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {did.custid}
                      </Typography>
                    </TableCell>
                    <TableCell>{getTypeName(did.did_type)}</TableCell>
                    <TableCell>
                      {did.deletion_date ? new Date(did.deletion_date).toLocaleDateString() : 'Unknown'}
                    </TableCell>
                    <TableCell>{did.days_since_deletion || 0} days</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatus(did)}
                        color={getStatusColor(did)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {did.street_number ? (
                        <Box>
                          <Typography variant="caption">
                            {did.street_number} {did.street_name}
                          </Typography>
                          <br />
                          <Typography variant="caption">
                            {did.city}, {did.state}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No E911
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={isEligible ? 'Delete permanently' : 'Not eligible for deletion yet'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!isEligible}
                            onClick={() => {
                              setDidToDelete(did.did);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {dids.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No inactive DIDs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningIcon color="error" />
              <Typography>
                Are you sure you want to permanently delete DID {didToDelete}?
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              This will remove the DID from all systems and cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => didToDelete && handleDelete(didToDelete)}
            color="error"
            variant="contained"
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Bulk Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningIcon color="error" />
              <Typography>
                Are you sure you want to permanently delete {selectedDids.size} selected DIDs?
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              This will remove all selected DIDs from all systems and cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
          >
            Delete All Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DIDDeletion;